import type { AnySchema } from "yup/lib/schema";
import { ChangeEvent, useState } from "react";
import { FileEditAltIcon, IconButton, notification, Paragraph, PlusIcon, RichText, TextInput } from "@datagame/ui";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";

import { useGame } from "../../../../../../../contexts/game/GameContext";
import { useProject } from "../../../../../../../contexts/project/ProjectContext";
import type { Game } from "../../../../../../../gql/graphql";
import {
  ATTRIBUTE_CREATE,
  ATTRIBUTE_DELETE,
  ATTRIBUTE_UPDATE,
} from "../../../../../../../graphql/mutations/AttributeMutation";
import { AttributeElement } from "../SwiperSettingsForm/AttributeElement";
import { GAME_ALL } from "../../../../../../../graphql/queries/GameQueries";
import { Section } from "../../Common";
import { useYupValidation } from "../../../../../../../hooks/useYupValidation";

import styles from "./AttributesSection.module.scss";

export interface AttributesSectionProps {
  game: Game;
  validation?: AnySchema;
  isWordopolis?: boolean;
}

export const AttributesSection = ({ game, validation, isWordopolis }: AttributesSectionProps) => {
  const { t } = useTranslation();
  const { currentAttributes } = useGame();
  const { project: { id: projectId } = {} } = useProject();

  const [attributeName, setAttributeName] = useState<string | undefined>(undefined);
  const { validationError, showError, setShowError } = useYupValidation(attributeName, validation);

  const [createAttribute] = useMutation(ATTRIBUTE_CREATE, {
    onCompleted: () => {
      setAttributeName("");
    },
    onError: (error) => {
      notification.danger({
        title: t("common:error"),
        description: error.message,
      });
    },
    refetchQueries: [{ query: GAME_ALL, variables: { projectId } }],
  });

  const [deleteAttribute] = useMutation(ATTRIBUTE_DELETE, {
    refetchQueries: [{ query: GAME_ALL, variables: { projectId } }],
    onError: (error) => {
      notification.danger({
        title: t("common:error"),
        description: error.message,
      });
    },
  });
  const [updateAttribute] = useMutation(ATTRIBUTE_UPDATE, {
    refetchQueries: isWordopolis ? [{ query: GAME_ALL, variables: { projectId } }] : undefined,
  });

  const handleAddAttribute = async () => {
    if (validationError) {
      setShowError(true);
      return;
    }

    await createAttribute({
      variables: {
        data: {
          name: attributeName ?? "",
        },
        gameId: game.id,
      },
    });
  };

  const handleDeleteAttribute = async (id: string) => {
    try {
      await deleteAttribute({
        variables: {
          attributeId: id,
        },
      });
    } catch (error) {
      if ((error as Error).message.includes("attributeId_fkey")) {
        return notification.danger({
          title: t(`project:gameSettings.form.errors.attributes.title.${isWordopolis ? "word" : "card"}`),
          description: t(`project:gameSettings.form.errors.attributes.deleteActive.${isWordopolis ? "word" : "card"}`),
        });
      }

      throw error;
    }
  };

  const handleUpdateAttribute = (id: string, name: string) => {
    updateAttribute({
      variables: {
        attributeId: id,
        data: {
          name,
        },
      },
    });
  };

  const handleAttributeNameChange = (value: string) => {
    setShowError(false);
    setAttributeName(value);
  };

  const handleWordopolisAttributeNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setShowError(false);
    setAttributeName(event.target.value);
  };

  const title = isWordopolis
    ? t("project:gameSettings.form.attributesLabelWordopolis")
    : t("project:gameSettings.form.attributesLabel");
  const tip = isWordopolis
    ? t("project:gameSettings.form.attributesTipWordopolis")
    : t("project:gameSettings.form.attributesTip");

  const subheader = isWordopolis ? t("project:gameSettings.form.attributesSubheaderWordopolis") : undefined;

  return (
    <Section title={title} tip={tip} subheader={subheader}>
      {currentAttributes.map((attribute) => (
        <AttributeElement
          key={attribute.id}
          attribute={attribute}
          validation={validation}
          value={attribute.name}
          onDelete={handleDeleteAttribute}
          onEdit={handleUpdateAttribute}
          isWordopolis={isWordopolis}
        />
      ))}

      <div className={styles.attributeField}>
        <div className={styles.inputWrapper}>
          {isWordopolis ? (
            <TextInput
              size="small"
              icon={<FileEditAltIcon />}
              value={attributeName ?? ""}
              onChange={handleWordopolisAttributeNameChange}
              placeholder={t(`project:gameSettings.form.addAttributePlaceholder.word`) as string}
            />
          ) : (
            <RichText
              id="add-new"
              key="add-new"
              size="small"
              icon={<FileEditAltIcon />}
              value={attributeName}
              onChange={handleAttributeNameChange}
              placeholder={t(`project:gameSettings.form.addAttributePlaceholder.card`) as string}
            />
          )}

          <IconButton
            icon={<PlusIcon />}
            style="outline"
            className={styles.attributeAdd}
            onClick={handleAddAttribute}
          />
        </div>
        {showError && (
          <Paragraph size="xs" className={styles.errorMessage}>
            {validationError}
          </Paragraph>
        )}
      </div>
    </Section>
  );
};
